import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as energyhubApi from '../api/energyhub';

export function EnergyHubStatus() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canViewCompliance = hasPermission('compliance:read');
  const canAdmin = hasPermission('admin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canViewCompliance) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await energyhubApi.getIntegrationStatus(selectedOrg.id);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canViewCompliance]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTestConnection = async () => {
    if (!selectedOrg || testing) return;
    setTesting(true);
    try {
      const result = await energyhubApi.testConnection(selectedOrg.id);
      alert(`Connection successful! Latency: ${result.latencyMs}ms`);
      loadData();
    } catch (err) {
      alert('Connection test failed: ' + err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleTriggerSync = async () => {
    if (!selectedOrg || syncing) return;
    setSyncing(true);
    try {
      const result = await energyhubApi.triggerSync(selectedOrg.id);
      alert(`Sync triggered! ID: ${result.syncId}`);
      loadData();
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return { bg: '#c6f6d5', color: '#276749' };
      case 'disconnected': return { bg: '#fed7d7', color: '#c53030' };
      case 'degraded': return { bg: '#fefcbf', color: '#975a16' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return { bg: '#fed7d7', color: '#c53030' };
      case 'warning': return { bg: '#fefcbf', color: '#975a16' };
      case 'info': return { bg: '#bee3f8', color: '#2b6cb0' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canViewCompliance) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need compliance permissions to view EnergyHub integration status.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading EnergyHub status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <div style={styles.container}>
        <PageHeader
          title="EnergyHub Integration"
          subtitle="Demand response and load control integration"
        />
        <div style={styles.notConfigured}>
          <h3>Integration Not Configured</h3>
          <p>EnergyHub integration has not been set up for this organization.</p>
          <p>Contact your administrator to configure the integration.</p>
        </div>
      </div>
    );
  }

  const status = data.status;
  const statusStyle = getStatusColor(status.status);

  return (
    <div style={styles.container}>
      <PageHeader
        title="EnergyHub Integration"
        subtitle="Demand response and load control integration status"
      />

      <div style={styles.headerActions}>
        {canAdmin && (
          <>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              style={styles.actionButton}
              data-testid="test-connection-button"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              onClick={handleTriggerSync}
              disabled={syncing}
              style={styles.primaryButton}
              data-testid="trigger-sync-button"
            >
              {syncing ? 'Syncing...' : 'Trigger Sync'}
            </button>
          </>
        )}
      </div>

      <div style={styles.statusHeader} data-testid="status-header">
        <div style={styles.statusIndicator}>
          <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
            {status.status}
          </span>
          <span style={styles.apiVersion}>API {status.apiVersion}</span>
          <span style={styles.environment}>{status.environment}</span>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Sync Status</h4>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Last Sync</span>
            <span style={styles.statValue}>{new Date(status.lastSyncAt).toLocaleString()}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Last Successful</span>
            <span style={styles.statValue}>{new Date(status.lastSuccessfulSync).toLocaleString()}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Sync Interval</span>
            <span style={styles.statValue}>{status.syncIntervalMinutes} minutes</span>
          </div>
        </div>

        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Device Status</h4>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Enrolled Devices</span>
            <span style={styles.statValue}>{status.enrolledDevices}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Active Devices</span>
            <span style={styles.statValue}>{status.activeDevices}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Events Processed</span>
            <span style={styles.statValue}>{status.totalEventsProcessed.toLocaleString()}</span>
          </div>
        </div>

        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Health (24h)</h4>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Errors</span>
            <span style={{ ...styles.statValue, color: status.errorCount24h > 0 ? '#c53030' : '#276749' }}>
              {status.errorCount24h}
            </span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Warnings</span>
            <span style={{ ...styles.statValue, color: status.warningCount24h > 0 ? '#975a16' : '#276749' }}>
              {status.warningCount24h}
            </span>
          </div>
        </div>

        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Features</h4>
          <div style={styles.featureList}>
            <div style={styles.featureItem}>
              <span style={status.features.loadControl ? styles.featureEnabled : styles.featureDisabled}>
                {status.features.loadControl ? '✓' : '✗'}
              </span>
              Load Control
            </div>
            <div style={styles.featureItem}>
              <span style={status.features.demandResponse ? styles.featureEnabled : styles.featureDisabled}>
                {status.features.demandResponse ? '✓' : '✗'}
              </span>
              Demand Response
            </div>
            <div style={styles.featureItem}>
              <span style={status.features.telemetry ? styles.featureEnabled : styles.featureDisabled}>
                {status.features.telemetry ? '✓' : '✗'}
              </span>
              Telemetry
            </div>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Credentials</h4>
        <div style={styles.card}>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Client ID</span>
            <span style={styles.statValue}><code>{status.credentials.clientId}</code></span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Secret Configured</span>
            <span style={styles.statValue}>{status.credentials.hasSecret ? 'Yes' : 'No'}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Expires</span>
            <span style={styles.statValue}>{new Date(status.credentials.expiresAt).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {data.recentErrors && data.recentErrors.length > 0 && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Recent Errors & Warnings</h4>
          <div style={styles.tableContainer}>
            <table style={styles.table} data-testid="errors-table">
              <thead>
                <tr>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Severity</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Message</th>
                  <th style={styles.th}>Endpoint</th>
                </tr>
              </thead>
              <tbody>
                {data.recentErrors.map(err => {
                  const sevStyle = getSeverityColor(err.severity);
                  return (
                    <tr key={err.id} style={styles.tr}>
                      <td style={styles.td}>{new Date(err.timestamp).toLocaleString()}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: sevStyle.bg, color: sevStyle.color }}>
                          {err.severity}
                        </span>
                      </td>
                      <td style={styles.td}><code>{err.code}</code></td>
                      <td style={styles.td}>{err.message}</td>
                      <td style={styles.td}><code style={styles.endpoint}>{err.endpoint}</code></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px' },
  notConfigured: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#718096' },
  headerActions: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  actionButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  primaryButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  statusHeader: { marginBottom: '1.5rem' },
  statusIndicator: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  statusBadge: { padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '600', textTransform: 'capitalize' },
  apiVersion: { fontSize: '0.875rem', color: '#718096', background: '#f7fafc', padding: '0.25rem 0.5rem', borderRadius: '4px' },
  environment: { fontSize: '0.875rem', color: '#718096', background: '#f7fafc', padding: '0.25rem 0.5rem', borderRadius: '4px', textTransform: 'capitalize' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardTitle: { margin: '0 0 1rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  statLabel: { fontSize: '0.875rem', color: '#718096' },
  statValue: { fontSize: '0.875rem', fontWeight: '500', color: '#2d3748' },
  featureList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' },
  featureEnabled: { color: '#276749', fontWeight: '600' },
  featureDisabled: { color: '#c53030', fontWeight: '600' },
  section: { marginBottom: '1.5rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  endpoint: { fontSize: '0.75rem', background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px' }
};
